import classNames from "classnames";
import { ColorName, Icon, Text } from "@zapier/design-system";

import { StepTitle } from "../../../../components/StepTitle";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";

import styles from "./AssetNodeSkeleton.module.css";
import { AssetPreview } from "./AssetPreview";
type Props = {
  id: string;
  isSelected: boolean;
  label: string;
  type: "table" | "zap" | "interface" | "chatbot";
  placeholder: string;
};

export function AssetNodeSkeleton({
  id,
  isSelected,
  label,
  type,
  placeholder,
}: Props) {
  return (
    <div className={classNames(styles.container, styles[type])}>
      <div className={styles.body}>
        <div className={styles.previewImage}>
          <AssetPreview type={type} />
        </div>
        <StepTitle
          nodeId={id}
          label={label}
          isSelected={isSelected}
          placeholder={placeholder}
          canNodeGetAiSuggestion={false}
          rows={2}
        />
      </div>
      <div className={styles.footer}>
        <div className={styles.iconContainer}>
          <Icon name="miscBoltAltFill" color="BrandOrange" size={16} />
        </div>
        <Text color={getContrastingTextColor(type)}>
          {capitalizeFirstLetter(type)}
        </Text>
      </div>
    </div>
  );
}

export function getContrastingTextColor(
  nodeType?: "table" | "zap" | "interface" | "chatbot",
): ColorName {
  // Get the background color hex based on the node type
  let backgroundColor = "";

  if (nodeType === "table") {
    backgroundColor = "#2b2358"; // --zds-colors-night from footer background
  } else if (nodeType === "interface") {
    backgroundColor = "#c1b7ff"; // From CSS
  } else if (nodeType === "zap") {
    backgroundColor = "#ffbf6e"; // From CSS
  } else if (nodeType === "chatbot") {
    backgroundColor = "#cde4e1"; // From CSS
  }

  // Normalize 3-digit hex to 6-digit
  if (/^#?[0-9A-F]{3}$/i.test(backgroundColor)) {
    backgroundColor = backgroundColor.replace(
      /^#?([0-9A-F])([0-9A-F])([0-9A-F])$/i,
      "#$1$1$2$2$3$3",
    );
  }

  // Remove "#" if present
  const cleanHex = backgroundColor.replace(/^#/, "");

  // Parse RGB components
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return appropriate color token based on luminance
  return luminance > 0.6 ? "BrandEarth" : "GrayWarm1";
}
